import React, { useState, useEffect } from 'react';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocaleAnchor } from '@/i18n/router';
import { useLocale } from '@/i18n/LocaleProvider';
import { writeSharedConsent } from '@/lib/sharedConsent';
import { deleteOptionalCookies, onOpenCookieSettings } from '@/lib/cookieSettings';

// Cookie consent. It must never hide the page's first screen: on phones it is a slim bar
// pinned to the bottom edge (one short line + two buttons, ~60px), and from `sm` up a small
// card in the bottom-right corner, above the Calendly badge, clear of the left-aligned hero
// CTAs. While the phone bar is open, `body.cookie-bar-open` hides the Calendly badge, which
// would otherwise sit on the bar or, lifted, on the hero CTA (see index.css).
// The footer's "Cookie settings" reopens it at any time (src/lib/cookieSettings.ts).

// The saved choice's analytics consent, or null when there is none (or storage is blocked).
const savedAnalyticsConsent = (): boolean | null => {
  try {
    const saved = JSON.parse(localStorage.getItem('cookieConsent') || 'null');
    return saved ? Boolean(saved.analytics) : null;
  } catch {
    return null;
  }
};

// The consent in force: the saved choice, else the region's default (index.html: granted outside opt-in regions).
const effectiveAnalyticsConsent = (): boolean => savedAnalyticsConsent() ?? window.gdConsentDefault === true;

const CookieConsent = () => {
  const { locale } = useLocale();
  const pt = locale === 'pt-BR';
  const [showConsent, setShowConsent] = useState(false);
  // The consent in force when the banner was reopened from the footer (null when it opened by itself).
  const [currentChoice, setCurrentChoice] = useState<boolean | null>(null);

  useEffect(() => {
    // Only show the banner if they haven't made a choice yet.
    let hasConsented: string | null = null;
    try {
      hasConsented = localStorage.getItem('cookieConsent');
    } catch {
      // Storage blocked: ask again (the choice just won't persist).
    }
    if (!hasConsented) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(
    () =>
      onOpenCookieSettings(() => {
        setCurrentChoice(effectiveAnalyticsConsent());
        setShowConsent(true);
      }),
    []
  );

  // Hide the Calendly badge on phones while the bar is open (index.css).
  useEffect(() => {
    if (!showConsent) return;
    document.body.classList.add('cookie-bar-open');
    return () => document.body.classList.remove('cookie-bar-open');
  }, [showConsent]);

  const saveConsent = (granted: boolean) => {
    const withdrawn = effectiveAnalyticsConsent() && !granted;
    const consent = {
      necessary: true,
      analytics: granted,
      marketing: granted,
      timestamp: new Date().toISOString()
    };
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(consent));
    } catch {
      // Storage blocked: the banner returns on the next visit.
    }
    // Shared with GizmoData Cloud's sign-in and sign-up pages (Clarity there follows this choice).
    writeSharedConsent(granted);
    setShowConsent(false);

    // Google Consent Mode v2: index.html sets the region's default and replays the saved choice
    // on later visits; this applies the new choice to the current page.
    if (typeof window.gtag !== 'undefined') {
      const value = granted ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        analytics_storage: value,
        ad_storage: value,
        ad_user_data: value,
        ad_personalization: value
      });
    }

    // Microsoft Clarity isn't loaded until the visitor accepts (index.html; production host only).
    if (granted) {
      window.gdLoadClarity?.();
    }

    // Withdrawing consent that was in force (an earlier Accept, or the default outside opt-in
    // regions): delete the cookies the tools already set, and reload so a Clarity that is
    // already recording stops (index.html won't load it again).
    if (withdrawn) {
      deleteOptionalCookies();
      window.location.reload();
    }
  };

  if (!showConsent) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label={pt ? 'Preferências de cookies' : 'Cookie preferences'}
      className="fixed inset-x-0 bottom-0 z-[9999] border-t border-border bg-background/95 px-4 py-2.5 shadow-[0_-4px_16px_rgba(0,0,0,0.12)] backdrop-blur-sm sm:inset-x-auto sm:bottom-16 sm:right-5 sm:w-[22rem] sm:rounded-xl sm:border sm:p-4 sm:shadow-lg"
    >
      <div className="flex items-center gap-3 sm:flex-col sm:items-stretch">
        <p className="flex-1 text-xs leading-snug text-muted-foreground">
          <Cookie className="mr-1.5 hidden h-4 w-4 align-[-3px] text-gizmo-600 dark:text-gizmo-400 sm:inline" aria-hidden="true" />
          {pt ? 'Cookies opcionais para análise de uso e anúncios' : 'Optional cookies for analytics and ads'}
          {currentChoice !== null && ` (${pt ? 'atualmente ' : 'currently '}${currentChoice ? (pt ? 'aceitos' : 'accepted') : (pt ? 'recusados' : 'declined')})`}.{' '}
          <LocaleAnchor href="/privacy-policy" className="underline hover:text-primary">{pt ? 'Política de privacidade' : 'Privacy Policy'}</LocaleAnchor>
        </p>
        <div className="flex flex-shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3 text-xs sm:flex-1"
            onClick={() => saveConsent(false)}
            aria-label={pt ? 'Recusar cookies opcionais (somente necessários)' : 'Decline optional cookies (necessary only)'}
          >
            {pt ? 'Recusar' : 'Decline'}
          </Button>
          <Button
            size="sm"
            className="h-8 bg-gizmo-600 px-3 text-xs text-white hover:bg-gizmo-700 sm:flex-1"
            onClick={() => saveConsent(true)}
            aria-label={pt ? 'Aceitar cookies opcionais' : 'Accept optional cookies'}
          >
            {pt ? 'Aceitar' : 'Accept'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
